import React, {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
    memo,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    Box,
    ClickAwayListener,
    Divider,
    FormControl,
    InputAdornment,
    List,
    ListItem,
    makeStyles,
    Paper,
    Popper,
    TextField,
    Typography,
    Link as MaterialLink,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'

import { RootState } from 'ducks/rootReducer'
import { setPlaybook } from 'ducks/actions'

import { useBoolean, useTextDebounce } from 'hooks'
import { getPlaybooks } from 'background/services'
import { AppContext } from 'app/AppProvider'

import { isMainSpace, generateSpaceIds } from 'UI/Pages/playbooks/PagePlaybooks'
import { TranscriptList } from './TranscriptList'
import { uiId } from 'services/utils'

const useStyles = makeStyles(theme => ({
    popper: {
        width: '100%',
        maxWidth: '1200px',
        zIndex: 3,
    },
    paper: {
        maxHeight: '600px',
        overflow: 'overlay',
    },
    label: {
        color: '#78909c',
        lineHeight: 1,
        marginBottom: theme.spacing(1),
        fontSize: theme.typography.pxToRem(14),
    },
    listItemLink: {
        color: '#212121',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        cursor: 'pointer',
        background: 'transparent',
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    resultTitle: {
        verticalAlign: 'middle',
    },
    inputBase: {
        flexDirection: 'row',
    },
    inputRoot: {
        '& input': {
            width: '100%',
        },
    },
    flexCenter: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputLabel: {
        padding: 0,
        margin: 0,
    },
    creatorLabel: {
        color: '#78909c',
        fontSize: theme.typography.pxToRem(12),
        marginLeft: '8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '130px',
    },
    timeLabel: {
        color: '#78909c',
        marginRight: '8px',
        whiteSpace: 'nowrap',
        fontSize: theme.typography.pxToRem(10),
    },
    title: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '630px',
        fontWeight: 'bold',
    },
}))

const pageSize = 5

type SearchPageProps = {
    index: number
    text: string
    hasMore?: {
        isTrue: boolean
        isFalse: boolean
        setTrue: () => void
        setFalse: () => void
    }
    applicationId: string
    spaceIds: string[]
}

const SearchPage = memo(
    ({ index, text, hasMore, applicationId, spaceIds }: SearchPageProps) => {
        const classes = useStyles()
        const dispatch = useDispatch()

        const { user } = useSelector((state: RootState) => state.user)

        const { openPlaybookMode, openPlaylistMode } = useContext(AppContext)

        const [data, setData] = useState<any>(null)

        useEffect(() => {
            getPlaybooks(
                data => {
                    setData(data)

                    const searchText = text.toLowerCase().trim()
                    const searchKey = `lastSearchedText/${user.uid}`
                    const lastSearchedText = sessionStorage.getItem(searchKey)

                    if (searchText === lastSearchedText) {
                        return
                    }

                    sessionStorage.setItem(searchKey, searchText)
                },
                {
                    size: pageSize,
                    page: index,
                    text,
                    apps: [applicationId],
                    method: 'search',
                    isMainSpace: isMainSpace(spaceIds),
                    spaceIds: generateSpaceIds(spaceIds),
                }
            )
        }, [index, text, applicationId, spaceIds, user.uid])

        const playbooks = data?.playbooks
        const hitsNumber = data?.nbHits

        const handleClick = playbook => {
            dispatch(setPlaybook(playbook))
            playbook?.isPlaylist ? openPlaylistMode() : openPlaybookMode()
        }

        const renderResultBox = () => {
            if (playbooks?.length === 0) {
                return (
                    <Box p={index === 0 ? 0 : 2}>
                        <Typography>No Match Found</Typography>
                    </Box>
                )
            }

            if (playbooks?.length) {
                return (
                    <>
                        {index === 0 && (
                            <Typography className={classes.label}>
                                Playbooks ({hitsNumber})
                            </Typography>
                        )}
                        <List component="div">
                            {playbooks.map(playbook => {
                                if (
                                    playbook._snippetResult?.transcript
                                        ?.matchLevel !== 'none'
                                ) {
                                    return (
                                        <ListItem
                                            className={classes.listItem}
                                            key={playbook.id}
                                        >
                                            <Box display="flex">
                                                <Box
                                                    display="flex"
                                                    alignItems="flex-start"
                                                >
                                                    <MaterialLink
                                                        key={playbook.id}
                                                        className={
                                                            classes.listItemLink
                                                        }
                                                        onClick={() => {
                                                            handleClick(
                                                                playbook
                                                            )
                                                        }}
                                                    >
                                                        <Box mr={2}>
                                                            <img
                                                                style={{
                                                                    width: '100px',
                                                                }}
                                                                src={
                                                                    playbook.img
                                                                }
                                                                alt="thumbnail"
                                                            />
                                                        </Box>
                                                    </MaterialLink>
                                                    <Box
                                                        display="flex"
                                                        alignItems="left"
                                                        flexDirection="column"
                                                        mr={2}
                                                    >
                                                        <MaterialLink
                                                            key={playbook.id}
                                                            className={
                                                                classes.listItemLink
                                                            }
                                                            onClick={() => {
                                                                handleClick(
                                                                    playbook
                                                                )
                                                            }}
                                                        >
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                            >
                                                                <Typography
                                                                    noWrap
                                                                    className={
                                                                        classes.title
                                                                    }
                                                                    component="span"
                                                                >
                                                                    {
                                                                        playbook.title
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    noWrap
                                                                    className={
                                                                        classes.creatorLabel
                                                                    }
                                                                    component="span"
                                                                >
                                                                    {
                                                                        playbook.creator_name
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                        </MaterialLink>
                                                        <Box>
                                                            <TranscriptList
                                                                playbook={
                                                                    playbook
                                                                }
                                                                classes={
                                                                    classes
                                                                }
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <MatchedTags playbook={playbook} />
                                        </ListItem>
                                    )
                                } else {
                                    return (
                                        <ListItem key={playbook.id}>
                                            <MaterialLink
                                                key={playbook.id}
                                                className={classes.listItemLink}
                                                onClick={() => {
                                                    handleClick(playbook)
                                                }}
                                            >
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                >
                                                    <Box
                                                        mr={2}
                                                        display="inline"
                                                    >
                                                        <img
                                                            style={{
                                                                width: '100px',
                                                            }}
                                                            src={playbook.img}
                                                            alt="thumbnail"
                                                        />
                                                    </Box>
                                                    <Typography
                                                        noWrap
                                                        style={{
                                                            fontWeight: 'bold',
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: playbook
                                                                ._snippetResult
                                                                ?.title?.value,
                                                        }}
                                                        component="span"
                                                    />
                                                    <Typography
                                                        noWrap
                                                        className={
                                                            classes.creatorLabel
                                                        }
                                                        component="span"
                                                    >
                                                        {playbook.creator_name}
                                                    </Typography>
                                                </Box>

                                                <MatchedTags
                                                    playbook={playbook}
                                                />
                                            </MaterialLink>
                                        </ListItem>
                                    )
                                }
                            })}
                        </List>
                        <Box my={1}>
                            <Divider />
                        </Box>
                    </>
                )
            }

            return (
                <Box p={index === 0 ? 0 : 2}>
                    <Typography>Loading...</Typography>
                </Box>
            )
        }

        useEffect(() => {
            if (hasMore !== undefined) {
                if (hitsNumber && hitsNumber > (index + 1) * pageSize) {
                    hasMore.setTrue()
                } else {
                    hasMore.setFalse()
                }
            }
        }, [hasMore, hitsNumber, index])

        return <>{renderResultBox()}</>
    }
)

type MatchedTagsProps = {
    playbook: {
        _snippetResult?: {
            tags?: Array<{
                matchLevel: string
                value: string
            }>
        }
    }
}
const MatchedTags = memo((props: MatchedTagsProps) => {
    const matchedResult = props.playbook._snippetResult?.tags?.find(
        tag => tag.matchLevel === 'full'
    )?.value

    if (!matchedResult) return null

    return (
        <Box ml={1} mr={2}>
            <Typography
                style={{
                    fontWeight: 'normal',
                }}
                dangerouslySetInnerHTML={{
                    __html: `#${matchedResult}`,
                }}
                component="span"
            />
        </Box>
    )
})

type Props = {
    predefinedSearch: string
    appFilter: {
        state: string
        defaultState: string | null
    }
    onSearchChange?: Dispatch<SetStateAction<null | string>>
    selectedAppName: string
    spaceIds: string[]
}

export const PlaybookSearch = memo(
    ({
        predefinedSearch,
        appFilter,
        onSearchChange,
        selectedAppName,
        spaceIds,
    }: Props) => {
        const classes = useStyles()
        const anchorRef = useRef(null)
        const inputRef = useRef<HTMLInputElement>(null)

        const [text, setText] = useState('')
        const debouncedText = useTextDebounce(text)

        const open = useBoolean()

        const hasMore = useBoolean()

        const [page, setPage] = useState(0)

        const getAppId = () => appFilter.state || appFilter.defaultState || ''

        const pages: Array<JSX.Element> = []

        for (let i = 0; i <= page; i++) {
            if (debouncedText?.length > 2) {
                pages.push(
                    <SearchPage
                        index={i}
                        key={i}
                        text={debouncedText}
                        hasMore={i === page ? hasMore : undefined}
                        applicationId={getAppId()}
                        spaceIds={spaceIds}
                    />
                )
            }
        }

        const handleChange = e => {
            e.persist()
            setText(e.target?.value)
            setPage(0)
        }

        const handleClick = () => {
            if (debouncedText?.length > 2) {
                open.setTrue()
            }
        }

        useEffect(() => {
            onSearchChange?.(debouncedText)
        }, [debouncedText, onSearchChange])

        useEffect(() => {
            inputRef?.current?.focus()
            setText(predefinedSearch || '')
        }, [predefinedSearch])

        const { setTrue, setFalse } = open

        useEffect(() => {
            if (debouncedText?.length > 2) {
                setTrue()
            } else {
                setFalse()
            }
        }, [debouncedText, setFalse, setTrue])

        return (
            <Box mb={2} style={{ position: 'relative' }}>
                <FormControl fullWidth>
                    <TextField
                        ref={anchorRef}
                        inputRef={inputRef}
                        value={text}
                        id={uiId('search')}
                        label={
                            <>
                                Search playbook name, transcript or tags{' '}
                                {selectedAppName && (
                                    <Typography
                                        noWrap
                                        style={{
                                            color: 'inherit',
                                        }}
                                        component="span"
                                    >
                                        for {selectedAppName}
                                    </Typography>
                                )}
                            </>
                        }
                        onClick={handleClick}
                        onChange={handleChange}
                        InputProps={{
                            classes: {
                                root: classes.inputRoot,
                                formControl: classes.inputBase,
                            },
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    className={classes.flexCenter}
                                >
                                    <SearchIcon
                                        style={{
                                            color: 'rgba(120, 144, 156, 0.87)',
                                        }}
                                    />
                                </InputAdornment>
                            ),
                        }}
                        InputLabelProps={{
                            className: classes.inputLabel,
                        }}
                    />
                </FormControl>

                <Popper
                    open={open.isTrue}
                    anchorEl={anchorRef?.current}
                    placement="bottom-start"
                    disablePortal
                    className={classes.popper}
                    modifiers={{
                        flip: {
                            enabled: false,
                        },
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: 'scrollParent',
                        },
                    }}
                >
                    <ClickAwayListener
                        onClickAway={() => {
                            setPage(0)
                            open.setFalse()
                        }}
                    >
                        <Paper elevation={3} className={classes.paper}>
                            <Box p={2} pb={1}>
                                {pages}
                            </Box>
                            {debouncedText?.length > 2 && hasMore.isTrue && (
                                <Box ml={4} mb={2} display="inline-block">
                                    <MaterialLink
                                        className={classes.listItemLink}
                                        onClick={event => {
                                            event.preventDefault()
                                            setPage(prevState => prevState + 1)
                                        }}
                                    >
                                        <Typography component="span">
                                            Show more
                                        </Typography>
                                    </MaterialLink>
                                </Box>
                            )}
                        </Paper>
                    </ClickAwayListener>
                </Popper>
            </Box>
        )
    }
)
